import axios from 'axios';

export const getBillingNotificationTarget = (notification) => {
  if (
    !notification ||
    !Number.isInteger(notification.clientId) ||
    notification.clientId < 1 ||
    !Number.isInteger(notification.invoiceId) ||
    notification.invoiceId < 1 ||
    typeof notification.navigationTarget !== 'string' ||
    !notification.navigationTarget.startsWith('/') ||
    notification.navigationTarget.startsWith('//')
  ) {
    return null;
  }

  try {
    const target = new URL(notification.navigationTarget, 'https://billing.local');
    const clientIds = target.searchParams.getAll('clientId');
    const invoiceIds = target.searchParams.getAll('invoiceId');
    if (
      target.origin !== 'https://billing.local' ||
      target.pathname !== '/clients' ||
      target.hash ||
      [...target.searchParams.keys()].some((key) => key !== 'clientId' && key !== 'invoiceId') ||
      clientIds.length !== 1 ||
      invoiceIds.length !== 1 ||
      String(notification.clientId) !== clientIds[0] ||
      String(notification.invoiceId) !== invoiceIds[0]
    ) {
      return null;
    }
    return `/clients?clientId=${encodeURIComponent(notification.clientId)}&invoiceId=${encodeURIComponent(notification.invoiceId)}`;
  } catch {
    return null;
  }
};

const PAGE_SIZE = 100;
const eligibleStatuses = new Set(['Sent', 'Overdue']);

const isRealDateOnly = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
};

const assertNotAborted = (signal) => {
  if (signal?.aborted) {
    const error = new Error('Invoice request was aborted.');
    error.name = 'AbortError';
    throw error;
  }
};

export const getInvoiceNotifications = async (storeId, signal) => {
  const activePastDue = [];
  const seenIds = new Set();
  const todayUTC = new Date().toISOString().slice(0, 10);
  let count;
  let offset = 0;

  do {
    assertNotAborted(signal);
    const response = await axios.get(`${process.env.REACT_APP_API}/admin/invoices`, {
      params: { storeId, limit: PAGE_SIZE, offset },
      withCredentials: true,
      signal,
      billingNotificationRequest: true,
    });
    assertNotAborted(signal);
    const page = response?.data;
    if (
      page?.success !== true ||
      !Number.isSafeInteger(page.count) ||
      page.count < 0 ||
      !Array.isArray(page.invoices) ||
      page.invoices.length > PAGE_SIZE
    ) {
      throw new Error('Invalid invoice list response.');
    }
    if (count === undefined) count = page.count;
    if (page.count !== count) throw new Error('Invoice list count changed during pagination.');
    if (page.invoices.length !== Math.min(PAGE_SIZE, count - offset)) {
      throw new Error('Incomplete invoice list pagination.');
    }

    for (const invoice of page.invoices) {
      if (
        !invoice ||
        !Number.isSafeInteger(invoice.id) ||
        invoice.id < 1 ||
        seenIds.has(invoice.id) ||
        invoice.storeId == null ||
        String(invoice.storeId) !== String(storeId) ||
        !Number.isSafeInteger(invoice.clientId) ||
        invoice.clientId < 1 ||
        !invoice.client ||
        invoice.client.id !== invoice.clientId
      ) {
        throw new Error('Invalid or mismatched invoice in invoice list.');
      }
      seenIds.add(invoice.id);
      if (!eligibleStatuses.has(invoice.status)) continue;
      if (
        !isRealDateOnly(invoice.dueDate) ||
        (invoice.invoiceNumber != null &&
          (!Number.isInteger(invoice.invoiceNumber) || invoice.invoiceNumber < 1)) ||
        typeof invoice.client.name !== 'string' ||
        !invoice.client.name.trim() ||
        (invoice.client.contactEmail !== null && typeof invoice.client.contactEmail !== 'string') ||
        typeof invoice.totalAmount !== 'number' ||
        !Number.isFinite(invoice.totalAmount)
      ) {
        throw new Error('Invalid eligible invoice in invoice list.');
      }
      if (invoice.dueDate < todayUTC) {
        activePastDue.push({
          id: invoice.id,
          invoiceId: invoice.id,
          invoiceStatus: invoice.status,
          invoiceNumber: invoice.invoiceNumber,
          clientId: invoice.clientId,
          clientName: invoice.client.name,
          contactEmail: invoice.client.contactEmail,
          totalAmount: invoice.totalAmount,
          dueDate: invoice.dueDate,
          navigationTarget: `/clients?clientId=${encodeURIComponent(invoice.clientId)}&invoiceId=${encodeURIComponent(invoice.id)}`,
        });
      }
    }
    offset += page.invoices.length;
  } while (offset < count);

  assertNotAborted(signal);
  return {
    success: true,
    activePastDueCount: activePastDue.length,
    activePastDue,
    notifications: [],
    source: 'invoice-status',
  };
};
